// @ts-nocheck

import './style.css'
import * as THREE from 'three'

import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import {
  EffectComposer,
  FullScreenQuad,
} from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass'
import { TexturePass } from 'three/examples/jsm/postprocessing/TexturePass'
// import { LuminosityShader } from './shaders/luminance'
// import { SobelOperatorShader } from './shaders/sobel'
import { DitherShader } from './shaders/dither'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader'
import { RectAreaLightUniformsLib } from 'three/examples/jsm/lights/RectAreaLightUniformsLib'
import { RectAreaLightHelper } from 'three/examples/jsm/helpers/RectAreaLightHelper'
import { mergeBufferGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils'
import { CSG } from 'three-csg-ts'
import GUI from 'lil-gui'

import vs from './shaders/default-raw-vs.glsl'
import sobel from './shaders/sobel-three.glsl'
import luminance from './shaders/luminance.glsl'
import pen from './shaders/pen.glsl'

import { map, randomFloat, randomInt, TO_RAD } from './math'

// import envUrl from 'url:../public/pedestrian_overpass_1k.hdr'
// import envUrl from 'url:../public/studio_small_02_1k.hdr'
import envUrl from 'url:../public/zhengyang_gate_1k.hdr'
// import toyUrl from 'url:../public/stray.jpg'

import { ScratchShader } from './shaders/scratch'

// pt
import {
  PathTracingSceneGenerator,
  PathTracingRenderer,
  PhysicalPathTracingMaterial,
} from 'three-gpu-pathtracer'
import { PathTracingSceneWorker } from './workers/PathTracingSceneWorker'

/**
 *
 */
;(async () => {
  const gui = new GUI()

  let windowHeight
  let windowWidth
  let isRendering = true
  let isPreloading = true

  const isPt = true

  const isPortrait = window.fxrand() < 0.5 ? true : false
  const isPanoramic = window.fxrand() < 0.1
  const isVerticalPanoramic = !isPanoramic && window.fxrand() < 0.3
  const isSpecial = window.fxrand() < 0.03

  const zoomRange = isPanoramic
    ? [[40, 50]]
    : isVerticalPanoramic
    ? [[35, 50]]
    : isPortrait
    ? [[36, 45]]
    : [
        [12, 17],
        [37, 50],
      ]

  const zoomRd = randomInt(0, zoomRange.length - 1)

  const FACADE_COLOR = [0x3198d3, 0x335d2f, 0xd1452a, 0xfab416, 0xf7f0de]
  const FLOOR_COLOR = [0x55d5fa, 0x294b26, 0xb13b25, 0xd29712, 0xd9d3c5]
  const COLUMN_COLOR = [0x2685bf, 0x335d2f, 0xd1452a, 0xfab416, 0xf7f0de]
  const STAIR_COLOR = [0x55d5fa, 0x335d2f, 0xd1452a, 0xfab416, 0xf7f0de]
  const UNDER_LIGHT_COLOR = [0x0600ff, 0x63f355, 0xff173d, 0xf7fa16, 0x0050c7]

  const rdColor = isSpecial
    ? FACADE_COLOR.length - 1
    : randomInt(0, FACADE_COLOR.length - 1)

  const envIntensityRange = [0.15, 2]
  const envRotationRange = [
    [1.3, 2.4],
    [5.8, 2 * Math.PI],
  ]
  const lightIntensityRange = [0, 0.42]

  const rdRange = Math.round(window.fxrand())

  const lightGroup = new THREE.Group()

  const guiParams = {
    envIntensity: randomFloat(envIntensityRange[0], envIntensityRange[1], 3),
    envRotation: randomFloat(
      envRotationRange[rdRange][0],
      envRotationRange[rdRange][1],
      3
    ),
    lightIntensity: randomFloat(
      lightIntensityRange[0],
      lightIntensityRange[1],
      3
    ),
    zoom: randomInt(zoomRange[zoomRd][0], zoomRange[zoomRd][1]),
  }

  gui.add(
    guiParams,
    'envIntensity',
    envIntensityRange[0],
    envIntensityRange[1],
    0.001
  )
  gui.add(
    guiParams,
    'envRotation',
    envRotationRange[0][0],
    envRotationRange[1][1],
    0.1
  )
  gui.add(
    guiParams,
    'lightIntensity',
    lightIntensityRange[0],
    lightIntensityRange[1],
    0.001
  )
  gui.add(guiParams, 'zoom', 1, 200, 1)
  // .onChange(() => {})

  const updatePt = () => {
    // pt
    ptRenderer.material.environmentIntensity = guiParams.envIntensity
    ptRenderer.material.environmentRotation.setFromMatrix4(
      new THREE.Matrix4().makeRotationY(guiParams.envRotation)
    )

    lightGroup.children.forEach((el) => {
      el.material.forEach((mat, index) => {
        if (mat.transparent && mat.map) {
          const nMat = mat.clone()
          nMat.opacity = guiParams.lightIntensity
          mat = nMat
        }
      })
    })

    orthoCameraMain.zoom = guiParams.zoom
    orthoCameraMain.updateProjectionMatrix()

    requestAnimationFrame(() => {
      ptRenderer.reset()

      isRendering = true
    })
  }

  gui.onChange(updatePt)

  if (process.env.NODE_ENV === 'production') gui.destroy()
  else document.querySelector('.pre').classList.add('is-hidden')

  const isOrtho = true
  const frustumSize = 600

  const getOrientation = () => {
    if (isVerticalPanoramic) return 'Vertical Panoramic'
    if (isPanoramic) return 'Panoramic'
    if (isPortrait) return 'Portrait'

    return 'Landscape'
  }

  const getLight = () => {
    if (guiParams.envIntensity <= 0.2) return 'Night'
    if (guiParams.envIntensity >= 1.2) return 'Sunny'

    return 'Daylight'
  }

  const getZoom = () => {
    if (guiParams.zoom < 40) return 'Low'
    else if (guiParams.zoom > 45) return 'High'

    return 'Middle'
  }

  window.$fxhashFeatures = {
    Orientation: getOrientation(),
    Color: `#${FACADE_COLOR[rdColor].toString(16)}`,
    Lightening: getLight(),
    Zoom: getZoom(),
    //   FStop: fStop,
    //   'Environment intensity': guiParams.envIntensity,
    // grayed: grayedFeat(guiParams.grayed),
  }

  if (isPanoramic) {
    windowWidth = 1920
    windowHeight = (windowWidth / 16) * 9
  } else if (isVerticalPanoramic) {
    windowHeight = 1920
    windowWidth = (windowHeight / 16) * 9
  } else if (isPortrait) {
    windowHeight = 1920
    windowWidth = (windowHeight / 4) * 3
  } else {
    windowWidth = 1920
    windowHeight = (windowWidth / 4) * 3
  }

  const aspect = windowWidth / windowHeight

  const pixelRatio = Math.min(window.devicePixelRatio, 1)
  let saveNextFrame = false

  /**
   * Renderer
   */

  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    preserveDrawingBuffer: true,
  })
  renderer.outputEncoding = THREE.sRGBEncoding
  renderer.toneMapping = THREE.ACESFilmicToneMapping

  renderer.setPixelRatio(pixelRatio)
  renderer.setSize(windowWidth, windowHeight, false)

  renderer.domElement.id = 'three-canvas'
  document.body.appendChild(renderer.domElement)

  /**
   * Camera
   */

  const camera = new THREE.PerspectiveCamera(
    50,
    windowWidth / windowHeight,
    0.025,
    500
  )
  if (isPortrait) {
    camera.position.set(0, 0, 15)
  } else {
    // camera.position.set(
    //   randomFloat(18.42024663213547, 31.361663989331035, 14),
    //   randomFloat(42.13739807132287, 45.21240554473169, 14),
    //   randomFloat(44.560947907859735, 46.89063274826719, 14)
    // )
  }

  camera.updateProjectionMatrix()

  // const orthoHeight = orthoWidth / (windowWidth / windowHeight)
  const orthoCameraMain = new THREE.OrthographicCamera(
    (0.5 * frustumSize * aspect) / -2,
    (0.5 * frustumSize * aspect) / 2,
    (0.5 * frustumSize) / 2,
    (0.5 * frustumSize) / -2,
    0.1,
    500
  )

  // orthoCameraMain.zoom = isPanoramic
  //   ? 1
  //   : isVerticalPanoramic
  //   ? 1
  //   : isPortrait
  //   ? 1
  //   : 1
  // randomInt(zoomRange[zoomRd][0], zoomRange[zoomRd][1]) * 1.5
  // console.log(orthoCameraMain.zoom)
  const pX = randomInt(16, 32) /*map(
    orthoCameraMain.zoom,
    zoomRange[zoomRd][0],
    zoomRange[zoomRd][1],
    16,
    16
    // randomInt(45 - 5, 45 + 5),
    // randomInt(60 - 5, 60 + 5)
  )*/
  const pY = randomInt(25, 35) /*map(
    orthoCameraMain.zoom,
    zoomRange[zoomRd][0],
    zoomRange[zoomRd][1],
    25,
    25
    // randomInt(45 - 5, 45 + 5),
    // randomInt(75 - 5, 75 + 5)
  )*/
  const pZ = 40 /*map(
    orthoCameraMain.zoom,
    zoomRange[zoomRd][0],
    zoomRange[zoomRd][1],
    40,
    40
    // randomInt(75 - 5, 75 + 5),
    // randomInt(20 - 5, 20 + 5)
  )*/
  orthoCameraMain.position.set(pX, pY, pZ)

  orthoCameraMain.updateProjectionMatrix()

  /**
   * Controls
   */

  const controls = new OrbitControls(
    isOrtho ? orthoCameraMain : camera,
    renderer.domElement
  )

  controls.target.set(
    -0.2,
    0,
    -0.2
    //   randomFloat(24.74239401196706, 24.74239401196706, 14),
    //   randomFloat(13.480027756802189, 13.480027756802189, 14),
    //   randomFloat(21.53809283712092, 21.53809283712092, 14)
  )
  controls.update()

  controls.addEventListener('change', () => {
    // pt
    ptRenderer.reset()
    console.log('position', controls.object.position)
    // console.log('target', controls.target)

    isRendering = true
  })

  controls.enabled = !(process.env.NODE_ENV === 'production')

  /**
   * Events
   */

  // window.addEventListener('resize', onWindowResize)

  /**
   * Scene
   */

  const scene = new THREE.Scene()
  scene.background = new THREE.Color(0x292929)

  RectAreaLightUniformsLib.init()

  // pt

  const reso = 0.8
  const ptMaterial = new PhysicalPathTracingMaterial({
    // bounces: 3,
  })
  const ptRenderer = new PathTracingRenderer(renderer)
  ptRenderer.setSize(
    windowWidth * pixelRatio * reso,
    windowHeight * pixelRatio * reso
  )
  ptRenderer.camera = orthoCameraMain
  ptRenderer.material = ptMaterial

  ptRenderer.material.setDefine('FEATURE_GRADIENT_BG', 1)
  updatePt()

  const fsQuad = new FullScreenQuad(
    new THREE.MeshBasicMaterial({
      map: ptRenderer.target.texture,
    })
  )
  //

  /**
   * Composer
   */

  const geoFullScreenPlane = new THREE.PlaneGeometry(2, 2)

  const renderTarget = new THREE.WebGLRenderTarget(
    windowWidth * pixelRatio,
    windowHeight * pixelRatio
  )
  const renderTargetGray = new THREE.WebGLRenderTarget(
    windowWidth * pixelRatio,
    windowHeight * pixelRatio
  )
  const sceneFs = new THREE.Scene()

  const renderTargetSobel = new THREE.WebGLRenderTarget(
    windowWidth * pixelRatio,
    windowHeight * pixelRatio
  )
  const sceneSobel = new THREE.Scene()

  const orthoCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)

  const meshPlane = new THREE.Mesh(
    geoFullScreenPlane,
    new THREE.ShaderMaterial({
      vertexShader: vs,
      fragmentShader: luminance,
      uniforms: {
        tDiffuse: {
          value: renderTarget.texture,
        },
      },
    })
  )

  sceneFs.add(meshPlane)

  const meshPlaneSobel = new THREE.Mesh(
    geoFullScreenPlane,
    new THREE.ShaderMaterial({
      vertexShader: vs,
      fragmentShader: sobel,
      uniforms: {
        tDiffuse: {
          value: renderTargetGray.texture,
        },
        resolution: {
          value: new THREE.Vector2(
            windowWidth * pixelRatio,
            windowHeight * pixelRatio
          ),
        },
      },
    })
  )

  sceneSobel.add(meshPlaneSobel)

  // const fs = new FullScreenQuad(
  //   new THREE.ShaderMaterial({
  //     vertexShader: vs,
  //     fragmentShader: pen,
  //     uniforms: {
  //       tDiffuse: {
  //         value: renderTarget.texture,
  //       },
  //       tSobel: {
  //         value: renderTargetSobel.texture,
  //       },
  //       tToy: {
  //         value: new THREE.TextureLoader().load(toyUrl),
  //       },
  //     },
  //   })
  // )

  // const composer = new EffectComposer(renderer)
  // const renderPass = new RenderPass(scene, isOrtho ? orthoCameraMain : camera)
  // const scratchPass = new ShaderPass(ScratchShader)
  // const luminosityPass = new ShaderPass(LuminosityShader)
  // const sobelPass = new ShaderPass(SobelOperatorShader)
  // const ditherPass = new ShaderPass(DitherShader)
  // const texturePassDiff = new TexturePass(renderTarget.texture)
  // const texturePass = new TexturePass(renderTargetSobel.texture)

  // scratchPass.uniforms.uStrength.value = randomFloat(0.01, 0.05, 3)
  // scratchPass.uniforms.uRand.value = window.fxrand()

  // sobelPass.uniforms['resolution'].value.x = windowWidth * pixelRatio
  // sobelPass.uniforms['resolution'].value.y = windowHeight * pixelRatio

  // composer.addPass(renderPass)
  // composer.addPass(outlinePass)
  // composer.addPass(ditherPass)
  // composer.addPass(luminosityPass)
  // composer.addPass(sobelPass)
  // composer.addPass(texturePassDiff)
  // composer.addPass(texturePass)

  // console.log(composer)

  // composer.addPass(scratchPass)

  /**
   * Events
   */

  document.addEventListener('keydown', (event) => {
    if (event.key.toLowerCase() === 's') {
      saveNextFrame = true
    }
  })

  /**
   * Environment
   */

  // const envPromise = new RGBELoader().loadAsync(envUrl)

  // const res = await Promise.all([envPromise])
  // const envTex = res[0]

  // envTex.mapping = THREE.EquirectangularReflectionMapping

  // scene.environment = envTex

  setup()

  function canvasLightGradient({ colorStart, colorEnd, side }) {
    const canvasOff = new OffscreenCanvas(200, 100)
    const ctxOff = canvasOff.getContext('2d')!

    const pattern = ctxOff.createLinearGradient(
      0,
      0,
      side === 'horizontal' ? canvasOff.width : 0,
      side === 'horizontal' ? 0 : canvasOff.height
    )
    pattern.addColorStop(0, colorStart)
    pattern.addColorStop(0.2, colorEnd)

    ctxOff.fillStyle = pattern
    ctxOff.fillRect(0, 0, canvasOff.width, canvasOff.height)

    const canvasMask = new OffscreenCanvas(200, 100)
    const ctxMask = canvasMask.getContext('2d')!

    const patternMask = ctxMask.createLinearGradient(
      0,
      0,
      side === 'horizontal' ? canvasMask.width : 0,
      side === 'horizontal' ? 0 : canvasMask.height
    )
    patternMask.addColorStop(0, '#000000')
    patternMask.addColorStop(0.5, '#ffffff')

    ctxMask.fillStyle = patternMask
    ctxMask.fillRect(0, 0, canvasMask.width, canvasMask.height)

    return { canvasOff, canvasMask }
  }

  function generateFloor({ epaisseur, sceneGroup, lightGroup }) {
    const grp = new THREE.Group()

    const { canvasOff: gradient, canvasMask } = canvasLightGradient({
      colorStart: `#${FACADE_COLOR[rdColor].toString(16)}`,
      colorEnd: '#0000ff',
    })

    const matSideI = new THREE.MeshStandardMaterial({
      map: new THREE.CanvasTexture(gradient),
      transparent: true,
      opacity: isSpecial
        ? 0.12
        : map(
            Math.min(guiParams.envIntensity, 0.3),
            envIntensityRange[0],
            0.3,
            0.1,
            0
          ),
      // opacity: 0,
    })

    matSideI.emissive = new THREE.Color(UNDER_LIGHT_COLOR[rdColor])
    // matSideI.emissiveIntensity = 1
    matSideI.emissiveMap = new THREE.CanvasTexture(canvasMask)

    const matSides = [
      matSideI,
      matSideI,
      new THREE.MeshStandardMaterial({
        // top
        transparent: true,
        opacity: 0,
      }),
      new THREE.MeshStandardMaterial({
        color: UNDER_LIGHT_COLOR[rdColor],
        // bot
      }),
      matSideI,
      matSideI,
    ]

    const tab = new Array(6)
    for (let i = 0; i < tab.length; i++) {
      tab[i] = []
      for (let j = 0; j < tab.length; j++) {
        tab[i][j] = false
      }
    }

    const margin = 0.4

    for (let i = 0; i < tab.length; i++) {
      for (let j = 0; j < tab[i].length; j++) {
        if (tab[i][j] === true) continue

        let w = 1
        let d = 1

        if (
          j < tab[i].length - 1 &&
          window.fxrand() < 0.4 &&
          tab[i][j + 1] === false
        ) {
          tab[i][j + 1] = true
          w = 2 + margin
        } else if (
          i < tab.length - 1 &&
          window.fxrand() < 0.4 &&
          tab[i + 1][j] === false
        ) {
          tab[i + 1][j] = true
          d = 2 + margin
        } else {
          tab[i][j] = true
        }

        const geo = new THREE.BoxGeometry(w, 5, d)
        const box = new THREE.Mesh(geo, new THREE.MeshNormalMaterial())
        box.position.set(
          j + w / 2 + j * margin,
          -2.5 + epaisseur,
          i + d / 2 + i * margin
        )
        box.updateMatrix()

        grp.add(box)

        // const m = new THREE.RectAreaLight(
        //   new THREE.Color(0xff0000),
        //   20, // randomFloat(0.2, 2, 3),
        //   w,
        //   d
        // )
        // m.rotateX(90 * TO_RAD)
        // m.position.copy(box.position)
        // m.position.y = -5
        // scene.add(m)

        const lightCube = box.clone()

        lightCube.scale.set(0.998, 1, 0.998)

        lightCube.material = matSides

        lightGroup.add(lightCube)
      }
    }

    return { boxes: grp }
  }

  function generateFacades({ boxes, blockDepth, blockHeight, epaisseur }) {
    const planesGroup = []
    const offset = -0.01

    boxes.forEach((box) => {
      const { width: w, depth: d } = box.geometry.parameters

      /* Facades */

      const fA = new THREE.Mesh(
        new THREE.BoxGeometry(w, blockHeight, blockDepth),
        new THREE.MeshStandardMaterial({
          color: FACADE_COLOR[rdColor],
        })
      )
      fA.position.set(
        box.position.x,
        -blockHeight / 2 + epaisseur / 2 + offset,
        box.position.z - d / 2 - blockDepth / 2
      )
      planesGroup.push(fA)

      /* NOT VISIBLE */

      const fB = fA.clone()
      // fB.material = new THREE.MeshBasicMaterial({
      // opacity: 0,
      // transparent: true,
      // })
      fB.position.set(
        box.position.x,
        -blockHeight / 2 + epaisseur / 2 + offset,
        box.position.z + d / 2 + blockDepth / 2
      )
      planesGroup.push(fB)

      /* */

      const fC = new THREE.Mesh(
        new THREE.BoxGeometry(blockDepth, blockHeight, d),
        new THREE.MeshStandardMaterial({
          color: FACADE_COLOR[rdColor],
        })
      )
      fC.position.set(
        box.position.x - w / 2 - blockDepth / 2,
        -blockHeight / 2 + epaisseur / 2 + offset,
        box.position.z
      )
      planesGroup.push(fC)

      const fD = fC.clone()
      fD.position.set(
        box.position.x + w / 2 + blockDepth / 2,
        -blockHeight / 2 + epaisseur / 2 + offset,
        box.position.z
      )
      planesGroup.push(fD)
    })

    return { planesGroup }
  }

  function generateWindows({ blockDepth, blockHeight, sizes, lightGroup }) {
    const cacheWindows = []
    const windowsPosition = []
    const windows = []
    const windowHeight = 0.2
    const marginTop = 0.15

    sizes.map((w, index) => {
      const iter = w === 1 ? 3 : 7

      const facade = new THREE.Mesh(
        new THREE.BoxGeometry(w, blockHeight, blockDepth),
        new THREE.MeshStandardMaterial({ color: FACADE_COLOR[rdColor] })
      )

      const geo = new THREE.BoxGeometry(w / iter - 0.15, windowHeight, 0.2)
      const mat = new THREE.MeshBasicMaterial({ color: 0x00ff00 })

      for (let j = 0; j < 14; j++) {
        for (let i = 0; i < iter; i++) {
          const windowOccur = new THREE.Mesh(geo, mat)
          windowOccur.position.set(
            (w / iter) * i - w / 2 + w / iter / 2,
            2.5 - windowHeight / 2 - marginTop - j * (windowHeight + marginTop),
            0.01
          )

          if (!windowsPosition[w.toString()]) windowsPosition[w.toString()] = []
          windowsPosition[w.toString()].push(windowOccur.position.clone())

          facade.add(windowOccur)
          const posA = new THREE.Vector3()
          windowOccur.getWorldPosition(posA)
          facade.remove(windowOccur)
          windowOccur.position.copy(posA)
          windowOccur.updateMatrix()

          windows[j * iter + i] = windowOccur
        }
      }

      const currWindows = windows.reduce((prev, val) => {
        return CSG.subtract(prev, val)
      }, facade)

      cacheWindows[w.toString()] = currWindows
    })

    return { cacheWindows, windowsPosition }
  }

  function drawArch({ facade }) {
    const w = 0.6
    const d = 0.2 * 4
    const h = 4.7

    const archShape = new THREE.Shape()
    archShape.moveTo(0, 0)
    archShape.lineTo(0, h)
    archShape.absarc(w / 2, h, w / 2, Math.PI, 0, true)
    archShape.lineTo(w, 0)
    archShape.closePath()

    const archGeometry = new THREE.ExtrudeGeometry(archShape, {
      depth: d,
      bevelEnabled: false,
    })

    const archMesh = new THREE.Mesh(
      archGeometry,
      new THREE.MeshNormalMaterial()
    )
    // archMesh.rotateY(90 * TO_RAD)
    // archMesh.position.set(-1, 1, 3)
    // scene.add(archMesh)

    facade.add(archMesh)

    archMesh.position.x = -w / 2
    archMesh.position.y = -(facade.geometry.parameters.height - h) - h / 2
    archMesh.position.z = -d / 2

    const pos = new THREE.Vector3()
    archMesh.getWorldPosition(pos)
    facade.remove(archMesh)
    archMesh.position.copy(pos)

    archMesh.updateMatrix()

    // scene.add(archMesh)

    const subRes = CSG.subtract(facade, archMesh)
    // subRes.scale.set(1, 1, 5)
    return { subRes, archMesh }
  }

  // function drawRoof() {
  //   const w = 0.5
  //   const h = 2.5
  //   const elipX = 0.9

  //   const shape = new THREE.Shape()
  //   shape.moveTo(0, 0)
  //   shape.lineTo(w, 0)
  //   shape.absellipse(
  //     w,
  //     -0.5,
  //     w * elipX,
  //     w * elipX,
  //     90 * TO_RAD,
  //     180 * TO_RAD,
  //     false
  //   )
  //   // shape.lineTo(w * (1 - elipX), -h)
  //   shape.lineTo(0, -h)
  //   shape.closePath()

  //   const roofGeometry = new THREE.ExtrudeGeometry(shape, {
  //     depth: 1,
  //     bevelEnabled: false,
  //   })

  //   const roofMesh = new THREE.Mesh(
  //     roofGeometry,
  //     new THREE.MeshNormalMaterial()
  //   )

  //   return roofMesh
  // }

  function drawStairs({
    steps = 10,
    fill = false,
    width = 1,
    height = 1,
    depth = 1,
  } = {}) {
    const shape = new THREE.Shape()

    shape.moveTo(0, 0)

    for (let i = 0; i < steps; i++) {
      shape.lineTo(i * (width / steps), (i + 1) * (height / steps))
      shape.lineTo((i + 1) * (width / steps), (i + 1) * (height / steps))
    }

    if (fill) shape.lineTo(width, 0)
    shape.closePath()

    const extrudeSettings = {
      depth,
      bevelEnabled: false,
    }

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings)

    const mesh = new THREE.Mesh(
      geometry,
      new THREE.MeshStandardMaterial({ color: STAIR_COLOR[rdColor] })
    )

    return mesh
  }

  function drawColumns() {
    // const h = 20 - 0.9
    const h = 20 - 1.6
    const geos = []

    // const grp = new THREE.Group()
    // const base = new THREE.Mesh(
    //   new THREE.BoxGeometry(1, 0.3, 1),
    //   new THREE.MeshStandardMaterial({ color: COLUMN_COLOR[rdColor] })
    // )
    // base.position.y = 0.3 / 2
    const base = new THREE.BoxGeometry(1, 0.3, 1)
    base.translate(0, 0.3 / 2, 0)
    geos.push(base)

    const smallBase = base.clone()
    // smallBase.scale.set(0.7, 0.5, 0.7)
    // smallBase.position.y = 0.3 + 0.15 / 2
    smallBase.scale(0.7, 0.5, 0.7)
    smallBase.translate(0, 0.3, 0)
    geos.push(smallBase)

    // const middle = new THREE.Mesh(
    //   new THREE.CylinderGeometry(0.25, 0.25, h),
    //   new THREE.MeshStandardMaterial({ color: COLUMN_COLOR[rdColor] })
    // )
    // middle.position.y = 0.3 + 0.15 + h / 2
    const middle = new THREE.CylinderGeometry(0.25, 0.25, h)
    middle.translate(0, 0.3 + 0.15 + h / 2, 0)
    geos.push(middle)

    const smallTop = smallBase.clone()
    // smallTop.position.y = 0.3 + 0.15 + h + 0.15 / 2
    smallTop.translate(0, h + 0.3 / 2, 0)
    geos.push(smallTop)

    const top = base.clone()
    // top.position.y = 0.3 + 0.15 + h + 0.15 + 0.3 / 2
    top.translate(0, 0.3 + 0.15 + h + 0.15, 0)
    geos.push(top)

    // grp.add(base)
    // grp.add(smallBase)
    // grp.add(middle)
    // grp.add(smallTop)
    // grp.add(top)

    // grp.scale.set(0.25, 0.25, 0.25)

    const merged = mergeBufferGeometries(geos)

    const mergedMesh = new THREE.Mesh(
      merged,
      new THREE.MeshStandardMaterial({ color: COLUMN_COLOR[rdColor] })
    )

    mergedMesh.scale.set(0.25, 0.25, 0.25)

    return mergedMesh
  }

  function drawTower() {
    return new THREE.Mesh(
      new THREE.BoxGeometry(0.8, 5, 0.6),
      new THREE.MeshBasicMaterial({ color: 0xff0000 })
    )
  }

  function setup() {
    const sceneGroup = new THREE.Group()

    scene.add(sceneGroup)
    sceneGroup.add(lightGroup)

    /* Generate floor */

    const epaisseur = 0.01

    const floor = new THREE.Mesh(
      new THREE.BoxGeometry(100, epaisseur, 100),
      new THREE.MeshStandardMaterial({
        color: FLOOR_COLOR[rdColor],
      })
    )

    const { boxes } = generateFloor({ epaisseur, sceneGroup, lightGroup })

    const box2 = boxes.clone()
    box2.position.set(-8 - 0.5, 0, -0.5)
    box2.rotateY(90 * TO_RAD)
    const box2GrpNoTransform = new THREE.Group()

    while (box2.children.length) {
      box2GrpNoTransform.attach(box2.children[0])
    }

    const box3 = boxes.clone()
    box3.position.set(-0.5, 0, 0)
    box3.rotateY(-90 * TO_RAD)
    const box3GrpNoTransform = new THREE.Group()

    while (box3.children.length) {
      box3GrpNoTransform.attach(box3.children[0])
    }

    const box4 = boxes.clone()
    box4.position.set(0, 0, -8 - 0.5)
    const box4GrpNoTransform = new THREE.Group()

    while (box4.children.length) {
      box4GrpNoTransform.attach(box4.children[0])
    }

    const res = [
      ...boxes.children,
      ...box2GrpNoTransform.children,
      ...box3GrpNoTransform.children,
      ...box4GrpNoTransform.children,
    ].reduce((prevSubMesh, mesh) => {
      return CSG.subtract(prevSubMesh, mesh)
    }, floor)

    scene.add(res)

    const blockHeight = 5
    const blockDepth = 0.2

    const { planesGroup } = generateFacades({
      boxes: boxes.children,
      blockHeight,
      blockDepth,
      epaisseur,
    })

    const sizes = planesGroup.reduce((prv, el) => {
      return [
        ...prv,
        Math.max(el.geometry.parameters.width, el.geometry.parameters.depth),
      ]
    }, [])

    const { cacheWindows, windowsPosition } = generateWindows({
      blockDepth,
      blockHeight,
      sizes: [...new Set(sizes)],
      lightGroup,
    })

    const facadesGroup = new THREE.Group()

    const col = drawColumns()

    // const roof = drawRoof()
    // roof.position.y = epaisseur / 2

    const tower = drawTower()

    let count = 0
    let isWindows = false
    let isArch = false
    let isColumns = false

    const archPosition = []

    const { canvasOff: gradient, canvasMask } = canvasLightGradient({
      colorStart: `#${FACADE_COLOR[rdColor].toString(16)}`, //visible edge
      colorEnd: '#c09a00',
    })

    const { canvasOff: gradientH, canvasMask: canvasMaskH } =
      canvasLightGradient({
        colorStart: `#${FACADE_COLOR[rdColor].toString(16)}`, //visible edge
        colorEnd: '#c09a00',
        side: 'horizontal',
      })

    const emissiveColor = new THREE.Color(0xc09a00)
    const lightOpa = 0.06

    const lightMat = new THREE.MeshStandardMaterial({
      map: new THREE.CanvasTexture(gradient),
      side: THREE.BackSide,
      // transparent: true,
      opacity: lightOpa,
      // depthTest: false,
      // depthWrite: false,
    })
    lightMat.emissive = emissiveColor
    lightMat.emissiveMap = new THREE.CanvasTexture(canvasMask)

    const lightMatInverted = lightMat.clone()
    lightMatInverted.map = lightMatInverted.map.clone()
    lightMatInverted.map.wrapT = THREE.RepeatWrapping
    lightMatInverted.map.repeat.y = -1
    lightMatInverted.emissive = emissiveColor
    lightMatInverted.emissiveMap = new THREE.CanvasTexture(canvasMask)
    lightMatInverted.emissiveMap.wrapT = THREE.RepeatWrapping
    lightMatInverted.emissiveMap.repeat.y = -1

    const lightMatH = new THREE.MeshStandardMaterial({
      map: new THREE.CanvasTexture(gradientH),
      side: THREE.BackSide,
      // transparent: true,
      opacity: lightOpa,
      // depthTest: false,
      // depthWrite: false,
    })
    lightMatH.emissive = emissiveColor
    lightMatH.emissiveMap = new THREE.CanvasTexture(canvasMaskH)

    const lightMatHInverted = lightMatH.clone()
    lightMatH.map = lightMatH.map.clone()
    lightMatHInverted.map.wrapS = THREE.RepeatWrapping
    lightMatHInverted.map.repeat.x = -1
    lightMatHInverted.emissive = emissiveColor
    lightMatH.emissiveMap = new THREE.CanvasTexture(canvasMaskH)
    lightMatH.emissiveMap.wrapS = THREE.RepeatWrapping
    lightMatH.emissiveMap.repeat.x = -1

    // matSideI.emissiveIntensity = 1

    const t = new THREE.MeshStandardMaterial({
      transparent: true,
      opacity: 0,
      // color: 0x00ff00,
      // depthTest: false,
      // depthWrite: false,
    })

    const opaque = new THREE.MeshStandardMaterial({
      color: 0xc09a00,
      side: THREE.BackSide,
      transparent: true,
      opacity: lightOpa * 3,
    })
    opaque.emissive = emissiveColor

    // lightMatH.emissive =

    //
    //
    // lightMat.emissive =
    // opaque.emissive =

    // lightMatH.emissiveMap = new THREE.CanvasTexture(canvasMask)
    // lightMatHInverted.emissiveMap = new THREE.CanvasTexture(canvasMask)
    // lightMatInverted.emissiveMap = new THREE.CanvasTexture(canvasMask)
    // lightMat.emissiveMap = new THREE.CanvasTexture(canvasMask)

    const mats = [
      lightMatH,
      lightMatHInverted,
      lightMatInverted,
      lightMat,
      t,
      opaque,
    ]

    const getDist = (facadeTested) => {
      archPosition.forEach((el) => {
        if (
          facadeTested.position.distanceTo(el.position) < 1 &&
          facadeTested.geometry.type === 'BoxGeometry'
        ) {
          // facadeTested.material.color = new THREE.Color(0xff0000)
          facadeTested.updateMatrix()

          const subRes = CSG.subtract(facadeTested, el.archMesh)
          facadesGroup.add(subRes)
          facadeTested.removeFromParent()
        }
      })
    }

    const getDistFromArch = (lightTested) => {
      archPosition.forEach((el) => {
        // console.log(lightTested.position.distanceTo(el.position))
        if (lightTested.position.distanceTo(el.position) < 1) {
          lightTested.material.forEach((mat, index) => {
            // const nMat = mat.clone()
            // nMat.opacity = 0
            // lightTested.material[index] = nMat
            // lightTested.material[index].needsUpdate = true
          })
        }
      })
    }

    planesGroup.forEach((facade, index) => {
      const box = boxes.children[index / 4]

      if (index % 4 === 0) {
        if (!isWindows && window.fxrand() > 0.6) {
          isWindows = true
          count = 0
        } else if (
          window.fxrand() > 0.85 &&
          box.geometry.parameters.width >= box.geometry.parameters.depth
        ) {
          /* Stairs */

          const stairs = drawStairs({
            height: 1.25,
            steps: randomInt(16, 24),
            width: box.geometry.parameters.width,
            depth: box.geometry.parameters.depth,
          })
          stairs.position.copy(box.position)
          stairs.position.x -= box.geometry.parameters.width / 2
          stairs.position.y = -1.25 + epaisseur / 2
          stairs.position.z -= box.geometry.parameters.depth / 2

          // stairs.rotateY(90 * TO_RAD)
          // stairs.position.z = 1

          // if (
          //   box.geometry.parameters.width === 1 &&
          //   box.geometry.parameters.depth === 1 &&
          //   window.fxrand() > 0.5
          // ) {
          //   stairs.rotateY(180 * TO_RAD)
          //   stairs.position.x = 1
          //   stairs.position.z = 1
          // }

          // stairs.rotateY(-90 * TO_RAD)
          // stairs.position.x = 1

          sceneGroup.add(stairs)
        } else if (
          !isArch &&
          window.fxrand() > 0.5 &&
          box.geometry.parameters.width === 1 &&
          box.geometry.parameters.depth === 1 &&
          facade.position.z > 0
        ) {
          isArch = true
          count = 0
        } else if (!isColumns && window.fxrand() > 0.8) {
          isColumns = true
          count = 0
        } /*else if (
          window.fxrand() > 0.9 &&
          box.geometry.parameters.width === 1 &&
          box.geometry.parameters.depth === 1
        ) {
          const towerClone = tower.clone()
          towerClone.position.copy(box.position)
          sceneGroup.add(towerClone)
          towerClone.position.y = 1.5
        }*/
      }

      if (isWindows) {
        if (index % 4 !== 1) {
          const { width: w, depth: d } = facade.geometry.parameters
          const cache = cacheWindows[Math.max(w, d).toString()].clone()

          cache.position.copy(facade.position)

          if (index % 4 > 1)
            cache.rotateY(90 * TO_RAD * (index % 2 === 1 ? -1 : 1))

          const windowWidth =
            Math.max(w, d) === 1 ? 1 / 3 - 0.15 : 2.4 / 7 - 0.15
          const geo = new THREE.BoxGeometry(windowWidth, 0.2, 0.2)

          for (let i = 0; i < randomInt(2, 4); i++) {
            if (guiParams.envIntensity > 0.2) break

            // const m = new THREE.Mesh(
            //   new THREE.PlaneGeometry(0.2, 0.2),
            //   new THREE.MeshBasicMaterial({
            //     color: 0xff0000,
            //     // depthWrite: false,
            //     // depthTest: false,
            //   })
            // )

            // const m = new THREE.RectAreaLight(
            //   new THREE.Color(0xd9ae00),
            //   1, // randomFloat(0.2, 2, 3),
            //   0.2,
            //   0.2
            // )
            // m.rotateY(180 * TO_RAD)

            const planeLight = new THREE.Mesh(geo, mats)

            // m.position.copy(facade.position)
            // m.rotation.copy(cache.rotation)

            // m.position.set(-Math.max(w, d) / 2, 2.5, 0)

            const rdPos =
              windowsPosition[Math.max(w, d).toString()][
                randomInt(
                  0,
                  windowsPosition[Math.max(w, d).toString()].length - 1
                )
              ]

            // if (w >= d) {
            //   planeLight.position.x = rdPos.x
            //   planeLight.position.z = -0.08
            // } else {
            //   planeLight.position.z -= 0.08
            // }

            // planeLight.position.y = rdPos.y

            // planeLight.rotateX(-90 * TO_RAD)

            planeLight.position.copy(rdPos)

            planeLight.scale.set(0.99, 0.99, 0.94)

            cache.add(planeLight)
            // lightGroup.attach(planeLight)
          }

          // const rectLight = new THREE.RectAreaLight(
          //   new THREE.Color(0xd9ae00),
          //   1,
          //   0.2,
          //   0.2
          // )

          // rectLight.position.copy(facade.position)
          // // rectLight.rotateZ(90 * TO_RAD)
          // scene.add(rectLight)
          // }

          facadesGroup.add(cache)
        } else {
          facadesGroup.add(facade)
        }

        count++
        if (count > 3) isWindows = false
      } else if (isArch) {
        if (count === 0) {
          const { subRes: arch, archMesh } = drawArch({ facade })
          archPosition.push({ position: facade.position, archMesh })
          sceneGroup.add(arch)
        } else {
          facadesGroup.add(facade)
        }

        count++
        if (count > 3) isArch = false
      } else {
        facadesGroup.add(facade)

        if (isColumns) {
          if (count === 0) {
            const w = box.geometry.parameters.width
            const d = box.geometry.parameters.depth

            const iterI = Math.ceil(w + 1)
            const iterJ = Math.ceil(d + 1)
            for (let j = 0; j < iterJ; j++) {
              for (let i = 0; i < iterI; i++) {
                const columnClone = col.clone()
                sceneGroup.add(columnClone)
                columnClone.position.copy(box.position)
                columnClone.position.y = -5
                columnClone.position.x +=
                  i * (w / iterI) - (w - 0.25) / 2 + (w / iterI - 0.25) / 2
                columnClone.position.z +=
                  j * (d / iterJ) - (d - 0.25) / 2 + (d / iterJ - 0.25) / 2
              }
            }
          }

          count++
          if (count > 3) isColumns = false
        }
      }
    })

    facadesGroup.children.forEach((el) => getDist(el))
    lightGroup.children.forEach((el) => getDistFromArch(el))
    sceneGroup.add(facadesGroup)

    /* Clones */

    const translateRotateOnScene = (objs) => {
      objs.forEach((el) => {
        if (el.type === 'Group') translateRotateOnScene(el.children)

        scene.attach(el)
      })
    }

    const sceneGroup2 = sceneGroup.clone()

    sceneGroup2.position.set(-8 - 0.5, 0, -0.5)
    sceneGroup2.rotateY(90 * TO_RAD)
    while (sceneGroup2.children.length) {
      translateRotateOnScene(sceneGroup2.children)
    }
    // scene.add(sceneGroup2)

    const sceneGroup3 = sceneGroup.clone()
    sceneGroup3.position.set(-0.5, 0, 0)
    sceneGroup3.rotateY(-90 * TO_RAD)
    while (sceneGroup3.children.length) {
      translateRotateOnScene(sceneGroup3.children)
    }
    // scene.add(sceneGroup3)

    const sceneGroup4 = sceneGroup.clone()
    sceneGroup4.position.set(0, 0, -8 - 0.5)
    while (sceneGroup4.children.length) {
      translateRotateOnScene(sceneGroup4.children)
    }
    // scene.add(sceneGroup4)

    // let transparentMat = null

    // sceneGroup.children.forEach((el, index) => {
    //   if (el.type === 'Group') {
    //     el.children.forEach((subEl, index) => {
    //       if (subEl.geometry.parameters) {
    //         console.log(subEl)
    //         if (!transparentMat) {
    //           transparentMat = subEl.material.clone()
    //           transparentMat.color = new THREE.Color(0xff0000)
    //         }
    //         subEl.material = transparentMat
    //       }
    //     })
    //   }
    // })

    /* Light */

    // const rectLight = new THREE.RectAreaLight(
    //   new THREE.Color(0xffd800),
    //   1,
    //   1,
    //   1
    // )
    // rectLight.position.set(3, 1, 3)
    // // rectLight.lookAt(3, 0, 2)
    // rectLight.rotateZ(90 * TO_RAD)

    // // const helper = new RectAreaLightHelper(rectLight)

    // // rectLight.add(helper)
    // scene.add(rectLight)

    if (!isPt) {
      /* Dir light */

      const dirLight = new THREE.DirectionalLight(0x50caef, 0.9)
      dirLight.position.set(5, 10, 12)
      dirLight.target.position.set(3, 0, 2)

      dirLight.target.updateMatrixWorld()

      scene.add(dirLight)
      // scene.add(dirLight.target)

      const helperDir = new THREE.DirectionalLightHelper(dirLight, 3)
      scene.add(helperDir)
    }

    // pt
    if (isPt) {
      const texture = new RGBELoader().load(envUrl, () => {
        ptRenderer.material.envMapInfo.updateFrom(texture)
      })

      const generator = new PathTracingSceneWorker()
      generator.generate(scene).then((result) => {
        const { bvh, textures, materials, lights } = result

        ptMaterial.bvh.updateFrom(bvh)
        ptMaterial.normalAttribute.updateFrom(bvh.geometry.attributes.normal)
        ptMaterial.tangentAttribute.updateFrom(bvh.geometry.attributes.tangent)
        ptMaterial.uvAttribute.updateFrom(bvh.geometry.attributes.uv)

        ptMaterial.materialIndexAttribute.updateFrom(
          bvh.geometry.attributes.materialIndex
        )
        ptMaterial.textures.setTextures(renderer, 2048, 2048, textures)
        ptMaterial.materials.updateFrom(materials, textures)

        ptMaterial.lights.updateFrom(lights)

        generator.dispose()

        draw()
      })
    } else {
      draw()
    }
  }

  function draw() {
    const rafId = requestAnimationFrame(draw)

    if (isRendering) {
      // controls.update()
      // renderer.setRenderTarget(renderTarget)
      if (!isPt) {
        renderer.render(scene, orthoCameraMain)
      }
      // renderer.setRenderTarget(renderTargetGray)
      // renderer.render(sceneFs, orthoCamera)
      // renderer.setRenderTarget(renderTargetSobel)
      // renderer.render(sceneSobel, orthoCamera)
      // renderer.setRenderTarget(null)
      // fs.render(renderer)
      if (isPt) {
        // pt
        ptRenderer.update()
        fsQuad.render(renderer)
      }
      // composer.render()

      if (Math.floor(ptRenderer.samples) > 80 /*&& window.isFxpreview*/) {
        window.fxpreview()
        isRendering = false
        // cancelAnimationFrame(rafId)
      }

      if (Math.floor(ptRenderer.samples) > 1 && isPreloading) {
        isPreloading = false
        document.querySelector('.pre').classList.add('is-hidden')
      }
    }

    if (saveNextFrame) {
      saveNextFrame = false
      const can = document.querySelector('#three-canvas') as HTMLCanvasElement
      const dataURL = can.toDataURL('image/png')
      const link = document.createElement('a')
      link.download = `pen-drawing-${Date.now()}.png`
      link.href = dataURL
      link.click()
    }
  }

  function onWindowResize() {
    isRendering = true

    if (isPanoramic) {
      windowWidth = window.innerWidth
      windowHeight = (windowWidth / 16) * 9
    } else if (isVerticalPanoramic) {
      windowHeight = window.innerHeight
      windowWidth = (windowHeight / 16) * 9
    } else if (isPortrait) {
      windowHeight = window.innerHeight
      windowWidth = (windowHeight / 4) * 3
    } else {
      windowWidth = window.innerWidth
      windowHeight = (windowWidth / 4) * 3
    }

    camera.aspect = windowWidth / windowHeight
    camera.updateProjectionMatrix()

    // const orthoHeight = orthoWidth / aspect

    // orthoCameraMain.updateProjectionMatrix()
    // orthoCameraMain.left = -windowWidth / 2
    // orthoCameraMain.right = windowWidth / 2
    // orthoCameraMain.top = windowHeight / 2
    // orthoCameraMain.bottom = -windowHeight / 2
    // orthoCameraMain.updateProjectionMatrix()

    renderer.setSize(windowWidth, windowHeight, false)
  }
})()
